import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import {
  Card,
  Table,
  Radio,
  Button,
  Spin,
  Typography,
  Input,
  Collapse,
  Upload as AntUpload,
  message,
  Alert,
} from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import { useDispatch, useSelector } from 'react-redux'
import { fetchVenueProducts } from 'src/features/VenueProductsSlice'
import { fetchAllRecipes } from 'src/features/RecipeBuilderSlice'
import { buildOriginalSnapshot } from 'src/services/allergenUpdateService'
import ShareWithGroupButton from 'src/screens/menu-builder/components/ShareWithGroupButton'
import { buildCsvString } from 'src/utils/csv'
import { saveAs } from 'file-saver'
import Papa from 'papaparse'
import { API } from 'src/utils/interceptor'
// import { buildAssignmentDiff, saveAssignmentsInBatches } from 'src/services/allergenBulkSaver'

const { Title, Text } = Typography
const { Panel } = Collapse

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
  width: 100%;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`

const Controls = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`

const SearchInput = styled(Input.Search)`
  width: 300px;

  .ant-input {
    border-radius: 6px;
  }

  .ant-input-search-button {
    border-radius: 0 6px 6px 0;
  }
`

const InstructionText = styled.div`
  background: #f6ffed;
  border: 1px solid #b4d820;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 24px;
`

const StyledTable = styled(Table)`
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  overflow: hidden;

  .ant-table-thead > tr > th {
    background-color: #f5f5f5;
    font-weight: bold;
    text-align: center;
    border: 1px solid #d9d9d9;
  }

  .ant-table-tbody > tr > td {
    text-align: center;
    border: 1px solid #d9d9d9;
  }

  .ant-table-thead > tr > th:first-child {
    border-left: 2px solid #d9d9d9;
  }

  .ant-table-tbody > tr > td:first-child {
    border-left: 2px solid #d9d9d9;
  }

  .ant-table-thead > tr > th:last-child {
    border-right: 2px solid #d9d9d9;
  }

  .ant-table-tbody > tr > td:last-child {
    border-right: 2px solid #d9d9d9;
  }

  .allergen-radio-group {
    display: flex;
    justify-content: center;
    gap: 8px;
  }
`

const LastUpdatedText = styled.div`
  color: #666;
  font-size: 13px;
  margin-bottom: 16px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
`

const DisclaimerAlert = styled(Alert)`
  margin-bottom: 16px;
  border-radius: 6px;

  .ant-alert-message {
    font-weight: 600;
    color: #d4380d;
  }

  .ant-alert-description {
    color: #595959;
    line-height: 1.5;
  }
`

const defaultAllergens = [
  'Celery',
  'Gluten',
  'Crustaceans',
  'Eggs',
  'Fish',
  'Lupin',
  'Milk',
  'Molluscs',
  'Mustard',
  'Nuts',
  'Peanuts',
  'Sesame seeds',
  'Soya',
  'Sulphites',
]

const ProductAllergensTab = ({ venueId }) => {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [products, setProducts] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [pendingChanges, setPendingChanges] = useState({
    products: new Map(),
  })
  const originalSnapshotRef = useRef(null)
  const [uploading, setUploading] = useState(false)

  const dispatch = useDispatch()
  const venueProducts = useSelector(
    state => state.venueProducts?.products || [],
  )
  const recipes = useSelector(state => state.recipeBuilder?.recipes || [])
  const venueTitle = useSelector(state => state.venue?.venue?.title)
  const user = useSelector(state => state.auth.account.name)
  const venueAllergensLastUpdatedAt = useSelector(
    state => state.venue?.venue?.allergensLastUpdatedAt,
  )
  const venueAllergensLastUpdatedBy = useSelector(
    state => state.venue?.venue?.allergensLastUpdatedBy,
  )
  // Load data on component mount and when venueId changes
  useEffect(() => {
    const loadData = async () => {
      if (venueId) {
        // console.log('Loading product allergen data...')
        setLoading(true)

        try {
          await dispatch(fetchVenueProducts(venueId))
          await dispatch(fetchAllRecipes(venueId))

          // Custom ingredients are now handled through recipes data
          // No need to fetch them separately since we removed the Custom Ingredients section
        } catch (error) {
          console.error('Error loading product allergen data:', error)
        } finally {
          setLoading(false)
        }
      }
    }

    loadData()
  }, [dispatch, venueId])

  // Process products from shopping list
  useEffect(() => {
    if (venueProducts && Array.isArray(venueProducts)) {
      // First, group products by title to handle duplicates
      const productsByTitle = {}
      venueProducts
        .filter(product => product && product.title)
        .forEach(product => {
          const title = product.title.trim()
          if (!productsByTitle[title]) {
            productsByTitle[title] = []
          }
          productsByTitle[title].push(product)
        })

      // For each title group, pick the best product (edited by user first, then first in list)
      const deduplicatedProducts = Object.values(productsByTitle).map(
        products => {
          // Sort by: 1) edited by user, 2) has allergens, 3) original order
          return products.sort((a, b) => {
            if (a.allergensEditedByUser && !b.allergensEditedByUser) return -1
            if (!a.allergensEditedByUser && b.allergensEditedByUser) return 1
            if ((a.allergens?.length || 0) > (b.allergens?.length || 0))
              return -1
            if ((a.allergens?.length || 0) < (b.allergens?.length || 0))
              return 1
            return 0
          })[0] // Take the first (best) product
        },
      )

      const sortedProducts = deduplicatedProducts
        .sort((a, b) => a.title.localeCompare(b.title))
        .map(product => {
          return {
            ...product,
            key: product._id || product.venueProductId,
            allergens: product.allergens || [],
            allergenTraces: product.allergenTraces || [],
          }
        })

      setProducts(sortedProducts)
    }
  }, [venueProducts])

  // Build original snapshot only when venueProducts are first loaded from server
  useEffect(() => {
    if (venueProducts.length > 0 && !originalSnapshotRef.current) {
      // Apply the same deduplication logic as in the products processing
      const productsByTitle = {}
      venueProducts
        .filter(product => product && product.title)
        .forEach(product => {
          const title = product.title.trim()
          if (!productsByTitle[title]) {
            productsByTitle[title] = []
          }
          productsByTitle[title].push(product)
        })

      const deduplicatedProducts = Object.values(productsByTitle).map(
        products => {
          return products.sort((a, b) => {
            if (a.allergensEditedByUser && !b.allergensEditedByUser) return -1
            if (!a.allergensEditedByUser && b.allergensEditedByUser) return 1
            if ((a.allergens?.length || 0) > (b.allergens?.length || 0))
              return -1
            if ((a.allergens?.length || 0) < (b.allergens?.length || 0))
              return 1
            return 0
          })[0]
        },
      )

      const serverProducts = deduplicatedProducts.map(product => ({
        ...product,
        key: product._id || product.venueProductId,
        allergens: product.allergens || [],
        allergenTraces: product.allergenTraces || [],
      }))
      originalSnapshotRef.current = buildOriginalSnapshot(serverProducts)
    }
  }, [venueProducts.length]) // Only depend on server data length

  // Handle allergen assignment for products - now just updates pending changes
  const handleProductAllergenChange = (productId, allergen, value) => {
    // console.log('Product allergen change:', productId, allergen, value)
    const product = products.find(
      p => p._id === productId || p.venueProductId === productId,
    )
    if (!product) {
      // console.log('Product not found:', productId)
      return
    }

    // Normalize the value to ensure it's lowercase
    const normalizedValue = String(value).toLowerCase() // 'yes' | 'no' | 'traces'

    // Update pending changes with individual cell changes
    setPendingChanges(prev => {
      const newPending = new Map(prev.products)
      const existingChanges = newPending.get(productId) || {}

      // Store the individual cell change
      existingChanges[allergen] = normalizedValue
      newPending.set(productId, existingChanges)

      return {
        ...prev,
        products: newPending,
      }
    })

    // Update local state for immediate UI feedback
    const updatedProducts = products.map(p => {
      if (p._id === productId || p.venueProductId === productId) {
        // Rebuild allergens and allergenTraces arrays based on current state + pending changes
        const currentAllergens = [...(p.allergens || [])]
        const currentTraces = [...(p.allergenTraces || [])]

        // Remove from both arrays first
        const allergenIndex = currentAllergens.indexOf(allergen)
        const traceIndex = currentTraces.indexOf(allergen)

        if (allergenIndex >= 0) currentAllergens.splice(allergenIndex, 1)
        if (traceIndex >= 0) currentTraces.splice(traceIndex, 1)

        // Add to appropriate array based on selection
        if (normalizedValue === 'yes') {
          currentAllergens.push(allergen)
        } else if (normalizedValue === 'traces') {
          currentTraces.push(allergen)
        }

        return {
          ...p,
          allergens: currentAllergens,
          allergenTraces: currentTraces,
        }
      }
      return p
    })
    setProducts(updatedProducts)
  }

  const handleSaveChanges = async () => {
    try {
      // console.log('Save changes clicked!')
      setSaving(true)

      if (!venueId) {
        console.error('No venue ID available for saving allergen changes')
        return
      }

      // Convert pending changes to the format expected by the API
      const assignments = []

      for (const [productId, changes] of pendingChanges.products) {
        for (const [allergen, value] of Object.entries(changes)) {
          // Use the original allergen name for consistency with product state
          // The backend will handle normalization when saving
          assignments.push({
            itemType: 'venueProduct',
            venueProductId: productId,
            allergen: allergen, // Use original name, not normalized
            value: value.toLowerCase(),
          })
        }
      }

      if (assignments.length === 0) {
        // console.log('No changes to save')
        setPendingChanges({ products: new Map() })
        return
      }

      // Group assignments by product to update each product once
      const productUpdates = new Map()

      for (const assignment of assignments) {
        const { venueProductId, allergen, value } = assignment

        if (!productUpdates.has(venueProductId)) {
          // Always use current product state - let the assignments logic handle the changes
          const product = products.find(
            p =>
              p._id === venueProductId || p.venueProductId === venueProductId,
          )
          if (product) {
            productUpdates.set(venueProductId, {
              allergens: [...(product.allergens || [])],
              allergenTraces: [...(product.allergenTraces || [])],
            })
          }
        }

        const productState = productUpdates.get(venueProductId)
        if (productState) {
          // CRITICAL FIX: Remove from both arrays first using case-insensitive comparison
          // This ensures we handle allergens regardless of capitalization (e.g., 'lupin' vs 'Lupin')
          const allergenLower = allergen.toLowerCase()
          const allergenIndex = productState.allergens.findIndex(
            a => a.toLowerCase() === allergenLower,
          )
          const traceIndex = productState.allergenTraces.findIndex(
            a => a.toLowerCase() === allergenLower,
          )

          if (allergenIndex >= 0)
            productState.allergens.splice(allergenIndex, 1)
          if (traceIndex >= 0) productState.allergenTraces.splice(traceIndex, 1)

          // Add to appropriate array based on value
          if (value === 'yes') {
            productState.allergens.push(allergen)
          } else if (value === 'traces') {
            productState.allergenTraces.push(allergen)
          }
        }
      }

      // Build products array for bulk update
      const productsArray = Array.from(productUpdates.entries()).map(
        ([productId, { allergens, allergenTraces }]) => ({
          productId,
          allergens,
          allergenTraces,
        }),
      )

      // Send all products in a single API call
      await API.patch('api2', `venue/${venueId}/products/allergens`, {
        body: { products: productsArray, updatedBy: user || 'Unknown User' },
      })

      // CRITICAL FIX: Ensure proper sequencing to prevent race condition
      // First, refresh venue products and wait for completion
      await dispatch(fetchVenueProducts(venueId))

      // Add a small delay to ensure Redux state is fully updated
      await new Promise(resolve => setTimeout(resolve, 100))

      // Then refresh recipes to use the fresh venue product data
      // This ensures recipe allergen calculations use the updated venue product allergens
      await dispatch(fetchAllRecipes(venueId))

      // Clear pending changes
      setPendingChanges({ products: new Map() })

      // Show success message
      message.success('Allergen changes saved successfully!')

      // console.log('Allergen changes saved successfully')
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'An error occurred while saving changes.'
      console.error('Error saving allergen changes:', msg, err)
    } finally {
      setSaving(false)
    }
  }

  // CSV Download functionality
  const handleDownloadCSV = () => {
    try {
      // Define allergen columns (14 standard allergens)
      const allergenColumns = [
        'Celery',
        'Gluten',
        'Crustaceans',
        'Eggs',
        'Fish',
        'Lupin',
        'Milk',
        'Molluscs',
        'Mustard',
        'Nuts',
        'Peanuts',
        'Sesame seeds',
        'Soya',
        'Sulphites',
      ]

      // Create CSV headers (VenueProduct ID is first column - hidden but used for matching)
      const headers = [
        'VenueProduct ID',
        'Product Name',
        'SKU',
        'Supplier',
        ...allergenColumns,
      ]

      // Create CSV rows from current products (only saved data, not pending changes)
      const csvRows = [headers]

      products.forEach(product => {
        const row = [
          product._id || product.id || '', // VenueProduct ID for matching on upload
          product.title || '',
          product.product?.sku || '',
          product.supplierTitle || '',
        ]

        // Add allergen values for each allergen column
        // CRITICAL FIX: Use case-insensitive matching like the UI table does
        // This ensures CSV download shows the same data as the UI table
        allergenColumns.forEach(allergen => {
          // Use case-insensitive matching to find allergens in arrays
          const allergenLower = allergen.toLowerCase()

          // Check if allergen is in definite allergens array (case-insensitive)
          const hasAllergen = product.allergens?.some(
            a => a.toLowerCase() === allergenLower,
          )

          // Check if allergen is in traces allergens array (case-insensitive)
          const hasTraces = product.allergenTraces?.some(
            a => a.toLowerCase() === allergenLower,
          )

          // "Yes" overrides "May Contain" - same logic as UI table
          if (hasAllergen) {
            row.push('Yes')
          } else if (hasTraces) {
            row.push('May Contain')
          } else {
            row.push('No')
          }
        })

        csvRows.push(row)
      })

      // Generate CSV string
      const csvContent = buildCsvString(csvRows)

      // Create filename with venue name and date
      const currentDate = new Date().toISOString().split('T')[0] // YYYY-MM-DD format
      const venueName = venueTitle
        ? venueTitle.replace(/[^a-zA-Z0-9]/g, '-')
        : 'venue'
      const filename = `${venueName}-allergen-data-${currentDate}-orders-made-simple.csv`

      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      saveAs(blob, filename)
    } catch (error) {
      console.error('Error downloading CSV:', error)
      message.error('Failed to download CSV file')
    }
  }

  // CSV Upload functionality
  const handleUploadCSV = async file => {
    if (uploading) {
      return false
    }

    setUploading(true)

    try {
      // Parse CSV file using PapaParse
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: async function (results) {
          const errors = []
          const newPendingChanges = {
            products: new Map(pendingChanges.products),
          }
          let updatedCount = 0

          // Process each row
          results.data.forEach((row, index) => {
            const rowNumber = index + 2 // +2 for header and 0-index

            const venueProductId = row['VenueProduct ID']
            const productName = row['Product Name']

            if (!venueProductId) {
              errors.push(`Row ${rowNumber}: Missing VenueProduct ID`)
              return
            }

            // Find the product in our current data
            const product = venueProducts.find(p => p._id === venueProductId)

            if (!product) {
              errors.push(
                `Row ${rowNumber}: Product ID "${venueProductId}" not found${
                  productName ? ` (${productName})` : ''
                }`,
              )
              return
            }

            // Process each allergen
            const productChanges = {}

            defaultAllergens.forEach(allergen => {
              const value = (row[allergen] || '').trim().toLowerCase()

              // Validate value
              if (
                value &&
                !['yes', 'no', 'may contain', 'maycontain'].includes(value)
              ) {
                const productName = row['Product Name'] || 'Unknown Product'
                const errorMsg = `Row ${rowNumber} (${productName}): Invalid value "${row[allergen]}" for ${allergen}. Must be "Yes", "No", or "May Contain"`
                errors.push(errorMsg)
                return
              }

              // Map value to internal format (empty values default to 'no')
              if (value === 'yes') {
                productChanges[allergen] = 'yes'
              } else if (value === 'may contain' || value === 'maycontain') {
                productChanges[allergen] = 'traces'
              } else {
                // Treat empty or 'no' as 'no' - this ensures ALL allergens are in pendingChanges
                productChanges[allergen] = 'no'
              }
            })

            // Add to pending changes if there are any changes for this product
            if (Object.keys(productChanges).length > 0) {
              newPendingChanges.products.set(venueProductId, productChanges)
              updatedCount++
            }
          })

          if (errors.length > 0) {
            // Show simple error message instead of modal
            const errorCount = errors.length
            const firstError = errors[0]
            message.error({
              content: `CSV upload failed: ${errorCount} error(s) found. ${firstError}. Please check your file and refer to the bulk editing instructions.`,
              duration: 10,
              style: {
                marginTop: '20vh',
              },
            })
            setUploading(false)
            return
          } else {
            // Update pending changes state
            setPendingChanges(newPendingChanges)

            // Update local products state for immediate UI feedback (same as manual changes)
            const updatedProducts = products.map(p => {
              const productChanges = newPendingChanges.products.get(p._id)
              if (productChanges) {
                // Rebuild allergens and allergenTraces arrays based on pending changes
                const currentAllergens = [...(p.allergens || [])]
                const currentTraces = [...(p.allergenTraces || [])]

                // Apply all pending changes for this product
                Object.entries(productChanges).forEach(([allergen, value]) => {
                  // Remove from both arrays first
                  const allergenIndex = currentAllergens.indexOf(allergen)
                  const traceIndex = currentTraces.indexOf(allergen)

                  if (allergenIndex >= 0)
                    currentAllergens.splice(allergenIndex, 1)
                  if (traceIndex >= 0) currentTraces.splice(traceIndex, 1)

                  // Add to appropriate array based on value
                  if (value === 'yes') {
                    currentAllergens.push(allergen)
                  } else if (value === 'traces') {
                    currentTraces.push(allergen)
                  }
                })

                return {
                  ...p,
                  allergens: currentAllergens,
                  allergenTraces: currentTraces,
                }
              }
              return p
            })
            setProducts(updatedProducts)

            message.success(
              `CSV loaded successfully! ${updatedCount} product(s) ready to update. Click "Save Changes" to apply.`,
            )
            setUploading(false)
          }
        },
        error: function (error) {
          console.error('Error parsing CSV:', error)
          message.error(
            'Failed to parse CSV file. Please check the file format.',
          )
          setUploading(false)
        },
      })
    } catch (error) {
      console.error('Error processing CSV:', error)
      message.error('Failed to process CSV file. Please try again.')
      setUploading(false)
    }

    // Prevent default upload behavior
    return false
  }

  // Check if there are any pending changes
  const hasPendingChanges = pendingChanges.products.size > 0

  // Filter products based on search term
  const filteredProducts = products.filter(product =>
    product.title.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  // Create columns for product table
  const createProductColumns = () => [
    {
      title: 'Product Name',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      fixed: 'left',
    },
    ...defaultAllergens.map(allergen => ({
      title: allergen,
      key: allergen,
      width: 140,
      render: (_, record) => {
        const productId = record._id || record.venueProductId

        // Check for pending changes first, then fall back to server data
        const pendingChange =
          pendingChanges?.products?.get(productId)?.[allergen]
        let hasAllergen, hasTraces

        if (pendingChange) {
          // Use pending change value
          hasAllergen = pendingChange === 'yes'
          hasTraces = pendingChange === 'traces'
        } else {
          // Use server data
          hasAllergen = record.allergens?.includes(allergen)
          hasTraces = record.allergenTraces?.includes(allergen)
        }

        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              alignItems: 'flex-start',
              padding: '8px 12px',
            }}
          >
            <Radio.Group
              className='allergen-radio-group'
              value={hasAllergen ? 'yes' : hasTraces ? 'traces' : 'no'}
              onChange={e =>
                handleProductAllergenChange(
                  record._id || record.venueProductId,
                  allergen,
                  e.target.value,
                )
              }
              size='small'
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px',
                  alignItems: 'flex-start',
                }}
              >
                <Radio value='yes'>Yes</Radio>
                <Radio value='traces'>May Contain</Radio>
                <Radio value='no'>No</Radio>
              </div>
            </Radio.Group>
          </div>
        )
      },
    })),
  ]

  // Don't render if Redux state isn't ready
  if (!venueProducts || !recipes) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size='large' />
          <div style={{ marginTop: '16px' }}>
            Loading product allergen data...
          </div>
        </div>
      </Container>
    )
  }

  return (
    <Container>
      <Header>
        <Title level={2}>
          Ingredient Allergens
          {hasPendingChanges && (
            <span
              style={{
                color: '#b4d820',
                fontSize: '14px',
                marginLeft: '8px',
                fontWeight: 'normal',
              }}
            >
              • Unsaved changes
            </span>
          )}
        </Title>
        <Controls>
          <ShareWithGroupButton
            offer={{ kind: 'allergens', venueId }}
            label='Share allergen flags…'
          />
          <SearchInput
            placeholder='Search products...'
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            onSearch={setSearchTerm}
            allowClear
            style={{ marginRight: '12px' }}
          />
          <Button
            type='primary'
            onClick={handleSaveChanges}
            loading={saving}
            disabled={!hasPendingChanges}
            style={{
              backgroundColor: hasPendingChanges ? '#b4d820' : undefined,
              borderColor: hasPendingChanges ? '#b4d820' : undefined,
            }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button
            type='default'
            onClick={handleDownloadCSV}
            style={{ marginLeft: '12px' }}
          >
            Download Allergen Data (CSV)
          </Button>
          <AntUpload
            accept='.csv'
            beforeUpload={handleUploadCSV}
            showUploadList={false}
            disabled={uploading}
          >
            <Button
              type='default'
              icon={<UploadOutlined />}
              loading={uploading}
              style={{ marginLeft: '12px' }}
            >
              {uploading ? 'Uploading...' : 'Upload Allergen Data (CSV)'}
            </Button>
          </AntUpload>
        </Controls>
      </Header>

      <InstructionText>
        <Text>
          Here you can assign allergens to the products in your shopping list.
          This list only includes products currently in your venue&apos;s
          shopping list. If you want to add more products, please go to the{' '}
          <strong>Catalogues tab</strong> in the OMS Dashboard and add them
          there.
        </Text>
        {searchTerm && (
          <div style={{ marginTop: '8px' }}>
            <Text type='secondary'>
              Showing {filteredProducts.length} of {products.length} products
              {searchTerm && ` matching "${searchTerm}"`}
            </Text>
          </div>
        )}
      </InstructionText>

      {/* Last Updated Timestamp */}
      {venueAllergensLastUpdatedAt && (
        <LastUpdatedText>
          Last updated:{' '}
          {new Date(venueAllergensLastUpdatedAt).toLocaleDateString('en-GB')} by{' '}
          {venueAllergensLastUpdatedBy || 'Unknown User'}
        </LastUpdatedText>
      )}

      {/* Liability Disclaimer */}
      <DisclaimerAlert
        message='⚠️ IMPORTANT: Allergen Data Responsibility Notice'
        description={
          <div>
            <p style={{ marginBottom: '8px' }}>
              This allergen management tool is provided for convenience only.
              You are solely responsible for the accuracy, completeness, and
              maintenance of all allergen information in your system.
            </p>
            <p style={{ marginBottom: '8px' }}>
              Before serving any food to customers, you must:
            </p>
            <ul style={{ marginBottom: '8px', paddingLeft: '20px' }}>
              <li>Independently verify all allergen data</li>
              <li>Check ingredient labels and supplier information</li>
              <li>Ensure compliance with local food safety regulations</li>
            </ul>
            <p style={{ marginBottom: '0' }}>
              OMS accepts no liability for any allergen-related incidents,
              customer safety issues, or regulatory violations arising from
              incorrect, incomplete, or outdated allergen data. The
              responsibility for food safety and allergen management remains
              entirely with your business.
            </p>
          </div>
        }
        type='warning'
        showIcon
        closable
      />

      {/* CSV Download Guidance - Collapsible */}
      <Collapse
        defaultActiveKey={['csv-instructions']}
        style={{ marginBottom: '16px' }}
        ghost
      >
        <Panel
          header={
            <Text strong style={{ color: '#389e0d', fontSize: '14px' }}>
              💡 CSV Bulk Editing Instructions
            </Text>
          }
          key='csv-instructions'
          style={{
            backgroundColor: '#f6ffed',
            border: '1px solid #b7eb8f',
            borderRadius: '6px',
          }}
        >
          <div style={{ padding: '0 16px 16px 0' }}>
            <Text style={{ fontSize: '13px', color: '#595959' }}>
              • <strong>Download:</strong> Click &quot;Download Allergen Data
              (CSV)&quot; to get your current allergen data
            </Text>
            <br />
            <Text style={{ fontSize: '13px', color: '#595959' }}>
              • <strong>Edit:</strong> Open the CSV file and change allergen
              values to: <strong>Yes</strong>, <strong>May Contain</strong>, or{' '}
              <strong>No</strong>
            </Text>
            <br />
            <Text style={{ fontSize: '13px', color: '#595959' }}>
              • <strong>Important:</strong> Don&apos;t change the VenueProduct
              ID, Product Name, SKU, or Supplier columns - they&apos;re used for
              identifying products
            </Text>
            <br />
            <Text style={{ fontSize: '13px', color: '#595959' }}>
              • <strong>Upload:</strong> Save your changes and upload the CSV
              file using the &quot;Upload Allergen Data (CSV)&quot; button
            </Text>
            <br />
            <Text style={{ fontSize: '13px', color: '#595959' }}>
              • <strong>Save:</strong> After uploading, click &quot;Save
              Changes&quot; to apply your allergen updates
            </Text>
          </div>
        </Panel>
      </Collapse>

      {loading ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size='large' />
            <div style={{ marginTop: '16px' }}>
              Loading product allergen data...
            </div>
          </div>
        </Card>
      ) : (
        <>
          <Card>
            <StyledTable
              dataSource={filteredProducts}
              columns={createProductColumns()}
              pagination={{ pageSize: 10 }}
              scroll={{ x: 1200 }}
              size='small'
            />
          </Card>
        </>
      )}
    </Container>
  )
}

export default ProductAllergensTab
